<template>
	<div>
		<div class="row" v-if="!this.data?.specialOperationRole">
			<div class="col-12">
				<p>{{ $t('operationRole.m.select-related-claims') }}:</p>
			</div>
		</div>
		<div class="row align-items-center">
			<div class="col-12 col-sm-8" v-if="!this.data?.specialOperationRole">
				<div class="nform">
					<nk-col-form-group :label="$t('operationRole.role') + ':'" :labelSizes="{ sm: 3 }" :dataSizes="{ sm: 9 }">
						<nk-dropdown v-model="selectedOperationRole" :options="componentOptions.operationRoles" :tabindex="101" ref="operationRoles" :data="operationRoles" @update:modelValue="operationRoleChange">
						</nk-dropdown>
					</nk-col-form-group>
				</div>
			</div>
			<div class="col-12 text-right" :class="{ 'col-sm-4': !this.data?.specialOperationRole }">
				<nk-col-form-group :label="$t('operationRole.select-all-only') + ':'" :labelSizes="{ sm: 6 }" :dataSizes="{ sm: 6 }">
					<nk-switch v-model="selectAll" :tabindex="103" @update:modelValue="(val) => { selectAllToggler(val) }" style="display:inline-block;margin-right:10px;">
					</nk-switch>
				</nk-col-form-group>
			</div>
		</div>
		<div class="mt-3">
			<div class="text-red" v-if="!selectedOperationRole">
				{{ $t("operationRole.select-role-for-show-claims") }}
			</div>
			<div v-else class="access-control-content">
				<div class="access-tree-container">
					<ag-tree ref="accessTree" :data="accessTreeData" :options="accessTreeOptions" :model-value="selectedTreeNodes" @selected="scheduleSelectionChanged" />
				</div>
			</div>
		</div>
	</div>
</template>
<script>
	import _ from 'lodash'
	import { post } from '~/plugins/nikan-api'
	import $sc from '~/plugins/shortcut.collection.js'
	import validators from '~/statics/validators'
	import Endpoints from '@nikan/endpoints'
	import AgTree from '~/components/tree/ag-tree.vue'
	import { routes } from "~/statics/routes"
	import { mapGetters } from "vuex"

	export default {
		props: {
			data: { type: Object, required: false },
		},
		modal: {
			helpTag: 'AcessControl'
		},
		components: {
			AgTree,
		},
		emits: ['operationRolesChanged'],
		data() {
			return {
				accessTreeData: [],
				selectedTreeNodes: [],
				isSyncingSelection: false,
				selectionTimeout: null,
				settings: {},
				selectAll: false,
				componentOptions: {
					operationRoles: {
						rowIdentifier: 'id',
						searchable: true,
						required: true,
						placeholder: this.$t('search-it'),
						display: (item) => `${item.name}`,
						searchableFields: ['name'],
					},
				},
				accessTreeOptions: {
					enableSearch: true,
					searchPlaceholder: this.$t('operationClaim.search'),
					searchableFields: ['name'],
					watchData: false,
					parentIdentifier: (data) => !this.isSelectableLeafNode(data) && data.id,
					agOptions: {
						getRowId: (params) => params.data.id,
						getDataPath: (data) => data?.hierarchy ?? [],
						rowModelType: 'clientSide',
						treeData: true,
						treeLevelRowColors: false,
						groupDefaultExpanded: 0,
						sideBar: 'none',
						rowHeight: 32,
						suppressRowHoverHighlight: true,
						focusAndSelect: { allowFocus: true },
						rowSelection: {
							mode: 'multiRow',
							checkboxLocation: 'autoGroupColumn',
							headerCheckbox: true,
							checkboxes: true,
							groupSelects: 'descendants',
							enableClickSelection: false,
						},
						autoGroupColumnDef: {
							headerName: this.$t('operationRole.claims'),
							valueFormatter: (params) => params.data?.name,
							minWidth: 300,
							floatingFilter: false,
							cellRendererParams: {
								suppressCount: true,
							},
						},
						columnDefs: [],
						onFirstDataRendered: () => {
							this.syncTreeSelection()
						},
						getContextMenuItems: () => {
							return [{
								name: this.$t('operationRole.downloadClaims'),
								action: () => {
									this.downloadRoleClaims()
								},
							}]
						}
					},
				},
				claims: [],
				operationRoles: [],
				validators: {
					name: validators('required'),
				},
				selectedOperationRole: null,
				privilegeTypes: [],
				saveAccessButton: {
					title: this.$t('save'),
					shortcut: $sc.find('save'),
					isPrimary: true,
					icon: 'save',
					handle: (button, page, e) => {
						e.preventDefault()
						this.save()
					},
					policyClaims: 'General_Access_Management',
				},
			}
		},
		computed: {
			...mapGetters({
				allProperties: 'properties/all',
			}),
		},
		beforeUnmount() {
			clearTimeout(this.selectionTimeout)
		},
		created() {
			this.getAllOperationClaims()
				.then((res) => {
					this.claims = res.items
					this.accessTreeData = this.buildAccessTreeData()
				})
				.then(() => {
					this.getAllOperationRoles().then((res) => {
						this.$emit('operationRolesChanged', res)
						this.operationRoles = res.filter((x) => !x.dedicatedUser)

						if ((this.data?.operationRole?.id) || (this.data?.specialOperationRole?.id)) {
							this.selectedOperationRole = this.operationRoles.find(
								(x) => x.id == this.data.operationRole?.id || x.id == this.data.specialOperationRole?.id
							)

							this.selectAll = this.selectedOperationRole.operationClaims.length === this.claims.length
							this.defer(() => this.syncTreeSelection(), 100)
						}
					})
				})
		},
		methods: {
			isSelectableLeafNode(node) {
				return node?.nodeType === 'claim' || node?.nodeType === 'menu-leaf'
			},
			getNodeClaimKeys(node) {
				if (node?.nodeType === 'claim')
					return [node.claimKey]
				if (node?.nodeType === 'menu-leaf')
					return node.policyToken || []
				return []
			},
			scheduleSelectionChanged() {
				if (this.isSyncingSelection || !this.selectedOperationRole)
					return

				clearTimeout(this.selectionTimeout)
				this.selectionTimeout = setTimeout(() => {
					this.applySelection()
				}, 100)
			},
			applySelection() {
				const grid = this.$refs.accessTree?.$refs?.grid
				if (!grid?.gridApi || !this.selectedOperationRole)
					return

				const currentKeys = new Set(
					(this.selectedOperationRole.operationClaims || []).map((claim) => claim.key)
				)
				const toggledOnKeys = new Set()
				const toggledOffKeys = new Set()

				grid.gridApi.forEachNode((node) => {
					const data = node.data
					if (!this.isSelectableLeafNode(data))
						return

					this.getNodeClaimKeys(data).forEach((key) => {
						const isInRole = currentKeys.has(key)
						if (node.isSelected() && !isInRole)
							toggledOnKeys.add(key)
						else if (!node.isSelected() && isInRole)
							toggledOffKeys.add(key)
					})
				})

				if (!toggledOnKeys.size && !toggledOffKeys.size) {
					this.syncTreeSelection()
					return
				}

				toggledOffKeys.forEach((key) => {
					const claim = this.claims.find((item) => item.key === key)
					if (claim)
						this.claimCheckChange(false, claim)
				})

				toggledOnKeys.forEach((key) => {
					if (toggledOffKeys.has(key))
						return
					const claim = this.claims.find((item) => item.key === key)
					if (claim)
						this.claimCheckChange(true, claim)
				})

				this.syncTreeSelection()
			},
			isNodeSelectedForKeys(node, selectedKeys) {
				const keys = this.getNodeClaimKeys(node)
				return keys.length > 0 && keys.every((key) => selectedKeys.has(key))
			},
			syncTreeSelection() {
				const grid = this.$refs.accessTree?.$refs?.grid
				if (!grid?.gridApi || !this.selectedOperationRole)
					return

				this.isSyncingSelection = true

				const selectedKeys = new Set(
					(this.selectedOperationRole.operationClaims || []).map((claim) => claim.key)
				)

				const selectedNodes = []
				grid.gridApi.deselectAll('setSelectedRowsEvent')
				grid.gridApi.forEachNode((node) => {
					const data = node.data
					if (!data || !this.isSelectableLeafNode(data))
						return

					if (this.isNodeSelectedForKeys(data, selectedKeys)) {
						node.setSelected(true, false, 'setSelectedRowsEvent')
						selectedNodes.push(data)
					}
				})

				this.selectedTreeNodes = selectedNodes
				this.defer(() => {
					this.isSyncingSelection = false
				}, 0)
			},
			isVisible(policyToken, commercialAccess) {
				if (!this.$nikan.checkCommercialAccess(commercialAccess))
					return false

				if (!this.$nikan.checkPermission(policyToken))
					return false

				return true
			},
			buildMenuNodeId(idPath) {
				return `menu_${idPath.join('__')}`
			},
			ensureUniqueTreeNodes(nodes) {
				const usedIds = new Set()

				return nodes.map((node) => {
					let id = node.id
					if (!usedIds.has(id)) {
						usedIds.add(id)
						return node
					}

					let suffix = 2
					while (usedIds.has(`${id}__${suffix}`))
						suffix++

					const uniqueId = `${id}__${suffix}`
					usedIds.add(uniqueId)
					return { ...node, id: uniqueId }
				})
			},
			buildAccessTreeData() {
				const nodes = []
				const groups = {}

				for (let i = 0; i < this.claims.length; i++) {
					const claim = this.claims[i]
					const [mainGroupName, subGroupName] = claim.key.split('_')

					if (this.data?.prefix) {
						if (mainGroupName !== this.extractGroup(this.data.prefix))
							continue
						if (this.data.prefix.indexOf('_' + subGroupName) === -1)
							continue
					}

					if (!groups[mainGroupName])
						groups[mainGroupName] = {}

					if (!groups[mainGroupName][subGroupName])
						groups[mainGroupName][subGroupName] = []

					groups[mainGroupName][subGroupName].push(claim)
				}

				Object.keys(groups).forEach((groupName) => {
					const groupId = `group_${groupName}`
					nodes.push({
						id: groupId,
						name: this.$t(`operationClaims.${groupName}`),
						nodeType: 'group',
						parent: null,
					})

					Object.keys(groups[groupName]).forEach((subGroupName) => {
						const subGroupId = `subgroup_${groupName}_${subGroupName}`
						nodes.push({
							id: subGroupId,
							name: this.$t(`operationClaims.${subGroupName}`),
							nodeType: 'subgroup',
							parent: { id: groupId },
						})

						groups[groupName][subGroupName].forEach((claim) => {
							nodes.push({
								id: `claim_${claim.key}`,
								name: this.$t(`operationClaims.${claim.key}`),
								nodeType: 'claim',
								claimKey: claim.key,
								claim,
								parent: { id: subGroupId },
							})
						})
					})
				})

				if (!this.data?.prefix)
					this.appendMenuNodes(nodes)

				return this.ensureUniqueTreeNodes(nodes)
			},
			getRouteClaimTokens(item) {
				const policyToken = _.isString(item?.policyToken) ? [item.policyToken] : (item?.policyToken || [])
				return policyToken.filter((token) => token && this.claims.some((claim) => claim.key === token))
			},
			appendMenuNodes(nodes) {
				const menusRootId = 'menu_root'
				const menuNodes = []

				const addRouteNodes = (items, parentId, parentToken, idPath = []) => {
					let addedCount = 0

					items.forEach((item) => {
						if (item.id === 'home')
							return

						const policyToken = this.getRouteClaimTokens(item)
						const hasChildren = item.items?.length > 0
						let visible = !item.hidden

						if (item.commercialAccess)
							visible = visible && this.isVisible(policyToken, item.commercialAccess)
						else if (parentToken)
							visible = visible && this.isVisible(policyToken, parentToken)

						if (!visible)
							return

						const currentIdPath = [...idPath, item.id]
						const nodeId = this.buildMenuNodeId(currentIdPath)

						if (hasChildren) {
							const branchIndex = menuNodes.length
							menuNodes.push({
								id: nodeId,
								name: this.$t(item.menu),
								nodeType: 'menu-branch',
								parent: { id: parentId },
							})

							const childParentToken = item.commercialAccess || parentToken
							const childCount = addRouteNodes(item.items, nodeId, childParentToken, currentIdPath)

							if (childCount === 0)
								menuNodes.splice(branchIndex, 1)
							else
								addedCount += 1 + childCount
						} else if (policyToken.length > 0) {
							menuNodes.push({
								id: nodeId,
								name: this.$t(item.menu),
								nodeType: 'menu-leaf',
								policyToken,
								parent: { id: parentId },
							})
							addedCount++
						}
					})

					return addedCount
				}

				const menuCount = addRouteNodes(_.cloneDeep(routes), menusRootId, null, [])
				if (menuCount === 0)
					return

				nodes.push({
					id: menusRootId,
					name: this.$t('operationClaims.Routes'),
					nodeType: 'menu-root',
					parent: null,
				})
				nodes.push(...menuNodes)
			},
			getAllOperationRoles() {
				return new Promise((resolve) => {
					if (this.data.specialOperationRole) {
						this.data.specialOperationRole.dedicatedUser = null
						resolve([this.data.specialOperationRole])
						return
					}
					post(Endpoints.getUrl('operationRole.list'), {
						loadRelatedData: true,
						includeDedicate: true,
					})
						.then((result) => {
							resolve(result.data.items)
						})
						.catch(() => {
						})
				})
			},
			getAllOperationClaims() {
				return new Promise((resolve) => {
					post(Endpoints.getUrl('operationClaim.list'), {})
						.then((result) => {
							resolve(result.data)
						})
						.catch(() => {
						})
				})
			},
			commitData() {
				return this.operationRoles
			},
			operationRoleChange() {
				this.selectAll = this.selectedOperationRole?.operationClaims.length === this.claims.length
				this.syncTreeSelection()
			},
			claimCheckChange(val, claim) {
				if (!this.selectedOperationRole) return

				if (val == false) {
					let index = this.selectedOperationRole.operationClaims.findIndex(
						(x) => x.key == claim.key
					)
					if (index > -1)
						this.selectedOperationRole.operationClaims.splice(index, 1)
				} else {
					let index = this.selectedOperationRole.operationClaims.findIndex(
						(x) => x.key == claim.key
					)
					if (index == -1) {
						this.operationRoles
							.find((x) => x.id == this.selectedOperationRole.id)
							.operationClaims.push(claim)
					}
				}
				this.selectAll = this.selectedOperationRole.operationClaims.length === this.claims.length
			},
			selectAllToggler(val) {
				if (!this.selectedOperationRole) return

				this.selectedOperationRole.operationClaims = []

				if (val) {
					for (var i = 0; i < this.claims.length; i++) {
						this.operationRoles
							.find((x) => x.id == this.selectedOperationRole.id)
							.operationClaims.push(this.claims[i])
					}
				}

				this.syncTreeSelection()
			},
			extractGroup(path) {
				return path.split('_')[0]
			},
			downloadRoleClaims() {
				const content = this.selectedOperationRole.operationClaims.map(x => x.key).join('\n')

				const blob = new Blob([content], { type: "text/plain" })
				const url = URL.createObjectURL(blob)

				const a = document.createElement("a")
				a.href = url
				a.download = 'Claims.txt'
				a.click()

				URL.revokeObjectURL(url)
			}
		},
	}
</script>
<style scoped>
	.access-control-content {
		width: 100%;
	}

	.access-tree-container {
		width: 100%;
		height: 500px;
		min-height: 400px;
	}
</style>
